/*
 * Tichetul unui articol: ce se stie despre el si unde se deschide.
 *
 * De ce exista fisierul asta. Aprobarea nu se mai face in magazin, ci pe tichet
 * (§4 din contract), deci ecranul cererii trebuie sa arate starea tichetului si
 * sa duca la el. Numai ca §3 descrie in continuare `GET /shop/requests/{id}` in
 * proza, fara sa fixeze numele campurilor. Alegerea intre formele posibile se
 * face intr-un singur loc - aici - ca sa se stearga dintr-un singur loc cand
 * contractul se fixeaza.
 *
 * Regula: camp lipsa = nu se deseneaza. Nu se inventeaza nici stare, nici
 * adresa. Un "Pending Approval" presupus ar fi mai rau decat o casuta goala:
 * omul ar crede ca stie unde e cererea lui.
 */

import { config } from '../api/config';
import type { RequestItem } from '../api/types';

export interface TicketRef {
  id: number | null;
  /** Starea din fluxul tipului de tichet, asa cum vine. `null` = nu se stie. */
  state: string | null;
  /** Eticheta gata scrisa de api, cand exista. */
  label: string | null;
  /** Adresa catre tichet, sau `null` cand nu se poate construi niciuna. */
  href: string | null;
  /**
   * Cine tine tichetul acum, si de cand.
   *
   * `null` cand nu se stie - inclusiv pe raspunsurile in forma veche, care nu
   * poarta campurile astea. Lipsa se deseneaza altfel decat un nume, deci nu se
   * inlocuieste cu un sir gol.
   */
  assignee: string | null;
  group: string | null;
  updated: string | null;
}

/**
 * Adresa tichetului.
 *
 * Prima varianta e cea trimisa de api: daca api-ul stie adresa, el are dreptate,
 * pentru ca el stie unde e instalata platforma. A doua e sablonul din
 * configurare (`VITE_TICKET_URL`), fiindca magazinul e o aplicatie separata si
 * nu are de unde sti singur hostul portalului. Fara niciuna din ele se intoarce
 * `null`, iar ecranul scrie numarul ca text - nu un link catre nicaieri.
 */
function hrefFor(id: number | null, url: unknown): string | null {
  const given = typeof url === 'string' ? url.trim() : '';
  if (given !== '') return given;
  if (id === null || config.ticketUrlTemplate === '') return null;
  return config.ticketUrlTemplate.replace('{id}', encodeURIComponent(String(id)));
}

/**
 * Textul de stare al unui tichet, cand exista chiar text.
 *
 * `unknown`, nu `string`: campurile de aici sunt "ce se poate sa trimita
 * api-ul", si el chiar trimite altceva - `ticket_status` vine ca NUMAR, id-ul
 * statusului. Tipul zicea `string`, dar tipul e o afirmatie despre corpul
 * raspunsului, nu o verificare a lui, si `.trim()` pe un numar darama tot
 * ecranul: exact asa a ramas alba fisa unei cereri dupa trimitere.
 *
 * Un numar se ignora, nu se transforma in text. `219` nu e o stare pe care s-o
 * citeasca cineva - e cheia catre ea - iar afisat ca atare ar arata a defect
 * pentru omul din fata ecranului si ar ascunde faptul ca eticheta adevarata
 * lipseste din raspuns.
 */
function normalise(value: unknown): string | null {
  if (typeof value !== 'string') return null;

  const text = value.trim();

  return text === '' ? null : text;
}

/**
 * Starea, si cand vine ca obiect.
 *
 * Api-ul trimite `ticket: { id, status: { id, name, type } }` - vezi
 * Views::ticket(). Citit ca text, obiectul cadea la `normalise` si iesea null,
 * asa ca ecranul se intorcea tacut la starea de magazin a articolului: scria
 * "In progress" acolo unde tichetul zicea "Pending Approval". Nimic nu se
 * strica vizibil, doar ca eticheta cea mai citita de pe ecran spunea altceva
 * decat adevarul.
 *
 * Se accepta amandoua formele fiindca ambele exista in instalari diferite, si
 * niciuna nu e scrisa in contract.
 */
function stateOf(value: unknown): string | null {
  if (value !== null && typeof value === 'object') {
    return normalise((value as { name?: unknown }).name);
  }

  return normalise(value);
}

/**
 * Ce stie articolul despre tichetul lui. `null` cand nu stie nimic - nici numar,
 * nici stare - adica atunci cand api-ul inca nu trimite nimic despre tichete.
 */
export function ticketOfItem(item: RequestItem): TicketRef | null {
  const grouped = item.ticket ?? null;

  const id = grouped?.id ?? item.ticket_id ?? null;
  const state =
    stateOf(grouped?.state) ??
    stateOf(grouped?.status) ??
    stateOf(item.ticket_state) ??
    stateOf(item.ticket_status);
  const label = normalise(grouped?.state_label ?? item.ticket_state_label);

  if (id === null && state === null) return null;

  return {
    id,
    state,
    label,
    href: hrefFor(id, grouped?.url ?? item.ticket_url),
    /* Cine tine tichetul acum, si de cand. Doar din obiectul grupat: campurile
       desfacute sunt forma veche a raspunsului, si ea n-a purtat asa ceva. */
    assignee: grouped?.assignee ?? null,
    group: grouped?.group ?? null,
    updated: grouped?.updated ?? null,
  };
}

/**
 * Subiectul unui tichet, fara prefixul tehnic al magazinului.
 *
 * Tichetele nascute din catalog primesc subiectul
 * „IT Service Catalog Request: <serviciu>" - acelasi in portal, in v4 si in
 * api, fiindca dupa el le recunoaste service desk-ul in lista lui. Pentru omul
 * care a cerut, prefixul nu spune nimic: stie ca a cerut din catalog. Se taie
 * aici, la afisare, si nu in baza - tichetele vechi il au deja, iar operatorii
 * il folosesc in continuare.
 *
 * Daca dupa prefix nu ramane nimic, se intoarce subiectul intreg: un titlu gol
 * e mai rau decat unul tehnic.
 */
const CATALOG_PREFIX = /^\s*IT Service Catalog Request:\s*/i;

export function ticketSubject(subject: string | null | undefined): string {
  const text = (subject ?? '').trim();
  const bare = text.replace(CATALOG_PREFIX, '').trim();

  return bare === '' ? text : bare;
}
